import type { AppLocale, DeepPartialMessageTree } from "@airange/shared/i18n-common";

export const microLabDrawerMessages: Record<AppLocale, DeepPartialMessageTree> = {
  "zh-CN": {
    microLab: {
      drawers: {
        scheme: {
          createTitle: "新建实验方案",
          editTitle: "编辑方案 · {name}",
          intro:
            "方案是设计态资产容器，先只维护基础信息；环境、手册、目标和步骤都已经进入独立的“方案版本管理”工作面。",
        },
        schemeShare: {
          title: "共享方案 · {name}",
          fallbackTitle: "共享方案",
          intro:
            "这里仅维护方案级 direct share grant；公开共享继续走共享审核入口，避免 direct share 和 public review 混写。",
          memberPickerTitle: "选择方案共享成员",
        },
        schemeVersion: {
          createTitle: "新建方案版本 · {name}",
          editTitle: "编辑版本 · {version}",
          overviewTitle: "版本概览",
          reviewTitle: "提交共享审核 · {name}",
          reviewSummaryTitle: "送审版本摘要",
          pickEnvironmentAssetTitle: "选择环境资产",
          pickTopologyAssetTitle: "选择拓扑资产",
          pickKsaTitle: "选择 KSA 分类",
        },
        library: {
          detailTitle: "共享条目详情 · {name}",
          detailFallbackTitle: "共享条目详情",
          reuseTitle: "复用共享方案 · {name}",
          reuseFallbackTitle: "复用共享方案",
          reuseIntro: "选择引用会保留与共享源的关系；选择克隆会创建一份可独立编辑的方案副本。",
          summaryTitle: "共享条目概览",
        },
        libraryReviews: {
          detailTitle: "共享审核详情 · {name}",
          detailFallbackTitle: "共享审核详情",
          reviewTitle: "审核共享条目 · {name}",
          reviewFallbackTitle: "审核共享条目",
        },
        common: {
          tagsCapabilitiesTitle: "标签与能力",
          tagPoolTitle: "实验标签",
          tagPoolEmpty: "当前方案还没有可读标签。",
          capabilitySummaryTitle: "能力摘要",
          capabilityEmpty: "当前方案还没有绑定正式 KSA 能力。",
          capabilityGroupCount: "{count} 项能力",
          metadataTitle: "元数据",
        },
        release: {
          createTitle: "新建实验发布",
          detailTitle: "发布详情 · {name}",
          detailFallbackTitle: "发布详情",
          summaryTitle: "发布概览",
          snapshotTitle: "发布快照",
          pickSourceSchemeVersionTitle: "选择来源方案版本",
          pickTargetMembersTitle: "选择目标成员",
          addTargetMembersTitle: "补充目标成员",
        },
        run: {
          detailTitle: "实验实例详情 · Run #{id}",
          detailFallbackTitle: "实验实例详情",
          overrideTitle: "人工改判 · Run #{id}",
          overrideFallbackTitle: "人工改判",
          resetTitle: "重置重开 · Run #{id}",
          resetFallbackTitle: "重置重开",
          currentInstanceTitle: "当前实例",
          checkProcessTitle: "Check 过程",
          historyTitle: "历史轮次",
          overrideAuditTitle: "改判留痕",
          resultSummaryTitle: "结果摘要",
          releaseSnapshotTitle: "发布快照",
        },
      },
    },
  },
  en: {
    microLab: {
      drawers: {
        scheme: {
          createTitle: "Create Lab Scheme",
          editTitle: "Edit Scheme · {name}",
          intro:
            "The scheme only keeps design-time base information here. Environment, manuals, objectives, and steps are maintained in the dedicated Scheme Versions workspace.",
        },
        schemeShare: {
          title: "Share Scheme · {name}",
          fallbackTitle: "Share Scheme",
          intro:
            "This drawer only maintains scheme-level direct share grants. Public sharing continues through shared review so direct sharing and public review do not mix.",
          memberPickerTitle: "Select Scheme Share Members",
        },
        schemeVersion: {
          createTitle: "Create Scheme Version · {name}",
          editTitle: "Edit Version · {version}",
          overviewTitle: "Version Overview",
          reviewTitle: "Submit Shared Review · {name}",
          reviewSummaryTitle: "Review Submission Summary",
          pickEnvironmentAssetTitle: "Select Environment Asset",
          pickTopologyAssetTitle: "Select Topology Asset",
          pickKsaTitle: "Select KSA Categories",
        },
        library: {
          detailTitle: "Shared Entry Details · {name}",
          detailFallbackTitle: "Shared Entry Details",
          reuseTitle: "Reuse Shared Scheme · {name}",
          reuseFallbackTitle: "Reuse Shared Scheme",
          reuseIntro:
            "Reference keeps the relationship with the shared source; clone creates an independently editable scheme copy.",
          summaryTitle: "Shared Entry Overview",
        },
        libraryReviews: {
          detailTitle: "Shared Review Details · {name}",
          detailFallbackTitle: "Shared Review Details",
          reviewTitle: "Review Shared Entry · {name}",
          reviewFallbackTitle: "Review Shared Entry",
        },
        common: {
          tagsCapabilitiesTitle: "Tags and Capabilities",
          tagPoolTitle: "Lab Tags",
          tagPoolEmpty: "No readable tags are configured for this lab yet.",
          capabilitySummaryTitle: "Capability Summary",
          capabilityEmpty: "No formal KSA capabilities are bound to this lab yet.",
          capabilityGroupCount: "{count} capabilities",
          metadataTitle: "Metadata",
        },
        release: {
          createTitle: "Create Lab Release",
          detailTitle: "Release Details · {name}",
          detailFallbackTitle: "Release Details",
          summaryTitle: "Release Overview",
          snapshotTitle: "Release Snapshot",
          pickSourceSchemeVersionTitle: "Select Source Scheme Version",
          pickTargetMembersTitle: "Select Target Members",
          addTargetMembersTitle: "Add Target Members",
        },
        run: {
          detailTitle: "Lab Instance Details · Run #{id}",
          detailFallbackTitle: "Lab Instance Details",
          overrideTitle: "Manual Override · Run #{id}",
          overrideFallbackTitle: "Manual Override",
          resetTitle: "Reset and Reopen · Run #{id}",
          resetFallbackTitle: "Reset and Reopen",
          currentInstanceTitle: "Current Instance",
          checkProcessTitle: "Check Process",
          historyTitle: "Attempt History",
          overrideAuditTitle: "Override Audit",
          resultSummaryTitle: "Result Summary",
          releaseSnapshotTitle: "Release Snapshot",
        },
      },
    },
  },
  ja: {
    microLab: {
      drawers: {
        schemeShare: {
          memberPickerTitle: "プラン共有メンバーを選択",
        },
        library: {
          reuseIntro: "参照は共有元との関係を保ち、複製は独立したコピーを作成します。",
          summaryTitle: "共有項目概要",
        },
      },
    },
  },
};
